import Image from "next/image";
import { useRouter } from "next/router";

import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Bugsnag from "@bugsnag/js";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";

import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons/faLinkedinIn";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons/faCheckCircle";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons/faExclamationCircle";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import LinkedInButton from "components/LinkedInBtn";
import { NotificationContext } from "components/Notification/NotificationContext";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import { useGlobalState } from "hooks/useGlobalState";
import { fetchApi } from "utils/baseFetcher";
import { emberAppBaseUrl, recaptchaKey } from "utils/envVariables";
import { loadResource } from "utils/externalLibraries";
import kebabCase from "utils/kebabCase";
import { setSkRedirectCookie } from "utils/setSkRedirectCookie";
import { expirationTime } from "consts/expireTime";
import bloombergLogo from "assets/logo-bloomberg-vector-white.svg";
import interactiveBrokerLogo from "assets/logo-interactive-brokers-white-stacked.svg";
import refinitivLogo from "assets/logo-refinitiv-vector-white.svg";
import sequoiaLogo from "assets/logo-sequoia-capital-vector-white.svg";
import sgxLogo from "assets/logo-SGX-vector-white.svg";
import societeGeneraleLogo from "assets/logo-societe-generale-vector-white.svg";

import style from "./style.module.scss";

const recaptchaURL = `https://www.google.com/recaptcha/api.js?render=${recaptchaKey}`;

type SignUpFormProps = {
  border?: boolean;
  insightId?: string;
  entityId?: string;
  accountId?: string;
  companyId?: string;
  collectionId?: string;
  type?: "normal" | "insight-signup" | "compact";
  onSwitch?: () => void;
  customFooter?: React.ReactNode;
  isModal?: boolean;
};

export const Form = ({
  insightId,
  entityId,
  accountId,
  companyId,
  collectionId,
  type,
  onSwitch,
}: Omit<SignUpFormProps, "border">) => {
  const router = useRouter();

  const { setNotification } = useContext(NotificationContext);

  const [signupSource] = useGlobalState("signupSource");

  const fstNameInpRef = useRef<HTMLInputElement>(null);
  const lstNameInpRef = useRef<HTMLInputElement>(null);
  const emailInpRef = useRef<HTMLInputElement>(null);

  const [recaptcha, setRecaptcha] = useState<any>(null);

  const [firstNameError, setFNError] = useState("");
  const [lastNameError, setLNError] = useState("");
  const [emailError, setEmailError] = useState("");

  const isSubmitting = false;

  useEffect(() => {
    try {
      loadResource("grecaptcha", recaptchaURL).then(
        (recaptchaInstance: any) => {
          recaptchaInstance.ready(() => {
            setRecaptcha(recaptchaInstance);
          });
        }
      );
    } catch (error: any) {}
  }, []);

  const signUp = useCallback(async () => {
    const firstName = fstNameInpRef.current?.value.trim();
    const lastName = lstNameInpRef.current?.value.trim();
    const email = emailInpRef.current?.value.trim();
    let haveError = false;

    if (firstName === "") {
      setFNError("is required");
      haveError = true;
    } else setFNError("");

    if (lastName === "") {
      setLNError("is required");
      haveError = true;
    } else setLNError("");

    if (email === "") {
      setEmailError("is required");
      haveError = true;
    } else if (
      !/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/.test(
        email || ""
      )
    ) {
      setEmailError("must be a valid email");
      haveError = true;
    } else setEmailError("");

    if (haveError) return;

    try {
      recaptcha
        .execute(recaptchaKey, { action: "register" })
        .then((recaptchaToken: string) => {
          const hash: any = {
            account: {
              firstName,
              lastName,
              email,
              previewPassInterest: "professional_investor",
            },
            recaptchaToken,
          };

          if (signupSource) {
            hash.account.sourceType = signupSource?.type;
            hash.account.sourceId = signupSource?.id;
          } else if (insightId) {
            hash.account.sourceType = "Insight";
            hash.account.sourceId = insightId;
          } else if (entityId) {
            hash.account.sourceType = "Entity";
            hash.account.sourceId = entityId;
          } else if (accountId) {
            hash.account.sourceType = "Account";
            hash.account.sourceId = accountId;
          } else if (companyId) {
            hash.account.sourceType = "Company";
            hash.account.sourceId = companyId;
          } else if (collectionId) {
            hash.account.sourceType = "Collection";
            hash.account.sourceId = collectionId;
          } else {
            hash.account.sourceType = router.asPath;
          }

          gtag?.("event", "sign_up_form_complete");

          fetchApi("/api/v2/sign-up", {
            method: "POST",
            body: JSON.stringify(hash),
          })
            .then(async (data: any) => {
              if (data && data.token) {
                const expireTime = expirationTime();

                data["authenticator"] = "authenticator:access-token";
                document.cookie = `ember_simple_auth-session=${encodeURIComponent(
                  JSON.stringify({ authenticated: data })
                )}; expires=${expireTime}; path=/`;

                setSkRedirectCookie(data);

                const accountId = data?.account_id;
                const account = await fetchApi(
                  `/api/v2/accounts/${accountId}?include=company`
                );
                if (account?.interfaceSetting?.requiresOnboarding) {
                  localStorage.setItem("firstLogin", "true");
                } else if (localStorage.getItem("firstLogin")) {
                  localStorage.removeItem("firstLogin");
                }

                const sourceLang = sessionStorage.getItem("sk-source-lang");
                if (sourceLang && !account?.interfaceSetting?.sourceLanguage) {
                  const payload = kebabCase({
                    data: {
                      id: account?.id,
                      attributes: {
                        interfaceSetting: {
                          sourceLanguage: sourceLang,
                        },
                      },
                      type: "accounts",
                    },
                  });
                  await fetchApi(`/api/v2/accounts/${account?.id}`, {
                    method: "PATCH",
                    body: JSON.stringify(payload),
                  });
                }

                if (
                  !["/", "/signin", "/signup"].includes(
                    window.location.pathname
                  )
                ) {
                  try {
                    sessionStorage.setItem(
                      "storage:signup",
                      JSON.stringify({
                        preSignupPath: `${window.location.pathname}`,
                      })
                    );
                  } catch (error: any) {
                    Bugsnag.notify(error);
                  }

                  gtag?.("event", "sign_up", {
                    event_callback: () => {
                      location.href = "/onboarding/v2";
                    },
                  });
                  setTimeout(() => {
                    location.href = "/onboarding/v2";
                  }, 0); // fallback
                } else {
                  gtag?.("event", "sign_up", {
                    event_callback: () => {
                      location.href = "/onboarding/v2";
                    },
                  });
                  setTimeout(() => {
                    location.href = "/onboarding/v2";
                  }, 0); // fallback
                }
              } else if (data && data.errors) {
                setNotification(data.errors, "error");
              }
            })
            .catch((err) => {
              setNotification("Failed to sign up. Please try again.", "error");
              console.error("Sign up error: ", err);
            });
        });
    } catch (error) {
      console.error("This is a error: ", error);
    }
  }, [
    accountId,
    collectionId,
    companyId,
    entityId,
    insightId,
    recaptcha,
    router.asPath,
    setNotification,
    signupSource,
  ]);

  if (type === "insight-signup") {
    return (
      <div>
        <label className="text-[16px] font-bold text-center w-full mb-[18px]">
          SMARTKARMA PREVIEW PASS
        </label>

        <LinkedInButton
          type="signUp"
          preview-pass-interest="professional_investor"
          insightId={insightId}
          entityId={entityId}
          accountId={accountId}
          companyId={companyId}
          customButton={(onClick: () => void) => (
            <button
              onClick={onClick}
              className="bg-[#2A66BC] text-white text-[18px] rounded h-[48px] w-full border-none flex items-center justify-center"
            >
              <div className="bg-white w-[20px] mr-[5px] h-[23px] flex items-center">
                <FontAwesomeIcon
                  icon={faLinkedinIn}
                  width={20}
                  height={20}
                  className="text-[#2A66BC] p-[1px] bg-white"
                />
              </div>
              <span className="font-medium">Sign Up with LinkedIn</span>
            </button>
          )}
        />

        <div className={style.or_divider}>
          <span>or</span>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            signUp();
          }}
        >
          <div className="flex flex-col text-[#1F1F20]">
            <label className="text-[14px]" htmlFor="first-name-input">
              First Name
              {firstNameError && (
                <span className={style.fieldError}>
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  {firstNameError}
                </span>
              )}
            </label>
            <input
              className="text-[16px] md:text-[15px] border-solid border-[#ccc] border-[1px] rounded h-[48px] w-full mb-[10px] px-[15px] focus:border-smart-teal focus:shadow outline-none"
              id="first-name-input"
              ref={fstNameInpRef}
            />
            <label className="text-[14px]" htmlFor="last-name-input">
              Last name
              {lastNameError && (
                <span className={style.fieldError}>
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  {lastNameError}
                </span>
              )}
            </label>
            <input
              className="text-[16px] md:text-[15px] border-solid border-[#ccc] border-[1px] rounded h-[48px] w-full mb-[10px] px-[15px] focus:border-smart-teal focus:shadow outline-none"
              id="last-name-input"
              ref={lstNameInpRef}
            />
            <label className="text-[14px]" htmlFor="email-input">
              Email
              {emailError && (
                <span className={style.fieldError}>
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  {emailError}
                </span>
              )}
            </label>
            <input
              className="text-[16px] md:text-[15px] border-solid border-[#ccc] border-[1px] rounded h-[48px] w-full mb-[10px] px-[15px] focus:border-smart-teal focus:shadow outline-none"
              id="email-input"
              ref={emailInpRef}
            />
          </div>
          <button
            className={classNames(
              "rounded border-solid border-smart-teal border-[1px] text-smart-teal  bg-transparent",
              "w-full text-[18px] leading-[22px] font-bold py-[12px] text-center",
              "mt-[10px]"
            )}
            type="submit"
            disabled={isSubmitting}
          >
            Sign Up with Email
          </button>
        </form>
        <label className="text-[12px] leading-[16px] text-[#999] mt-[14px] text-center w-full">
          By signing up, you agree to our
          <br />
          <a
            className="underline"
            href={`${emberAppBaseUrl}/home/terms-of-use/`}
          >
            Terms of Use
          </a>{" "}
          and{" "}
          <a
            className="underline"
            href={`${emberAppBaseUrl}/home/privacy-policy/`}
          >
            Privacy Policy
          </a>
          .
        </label>
      </div>
    );
  } else {
    return (
      <div className={style.body}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            signUp();
          }}
        >
          <label className={style.heading}>JOIN FOR FREE</label>
          <label className={style.sub_heading}>
            Access unlimited research summaries. Upgrade anytime.
          </label>
          <div className={style.form_fields}>
            <label htmlFor="first-name-input">
              First Name
              {firstNameError && (
                <span className={style.fieldError}>
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  {firstNameError}
                </span>
              )}
            </label>
            <input id="first-name-input" ref={fstNameInpRef} />
            <label htmlFor="last-name-input">
              Last name
              {lastNameError && (
                <span className={style.fieldError}>
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  {lastNameError}
                </span>
              )}
            </label>
            <input id="last-name-input" ref={lstNameInpRef} />
            <label htmlFor="email-input">
              Email
              {emailError && (
                <span className={style.fieldError}>
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  {emailError}
                </span>
              )}
            </label>
            <input id="email-input" ref={emailInpRef} />
          </div>
          <button className={style.btn} type="submit" disabled={isSubmitting}>
            SIGN UP
          </button>
        </form>
        <div className={style.or_divider}>
          <span>or</span>
        </div>

        <LinkedInButton
          type="signUp"
          preview-pass-interest="professional_investor"
          insightId={insightId}
          entityId={entityId}
          accountId={accountId}
          companyId={companyId}
        />

        <label className={style.small_text_light}>
          We treat your information with extreme care, and only use it to get in
          touch with you about our products and services. You may refer to our{" "}
          <a
            href={`${emberAppBaseUrl}/home/privacy-policy/`}
            title="privacy-policy"
          >
            {" "}
            Privacy Policy.{" "}
          </a>
        </label>
        <label className={style.small_text}>
          Already have an account?{" "}
          <span
            className="text-smart-teal hover:text-smart-teal cursor-pointer"
            onClick={() => (onSwitch ? onSwitch() : router.push("/signin"))}
          >
            Sign In Now
          </span>
        </label>
      </div>
    );
  }
};

const SignUpForm = ({
  border = true,
  insightId,
  entityId,
  accountId,
  companyId,
  collectionId,
  type,
  onSwitch,
  customFooter,
}: SignUpFormProps) => {
  const { t } = useTranslation();

  const { toggleModal } = useSignInSignUp();

  const form = (
    <Form
      type={type}
      insightId={insightId}
      entityId={entityId}
      accountId={accountId}
      companyId={companyId}
      collectionId={collectionId}
      onSwitch={onSwitch}
    />
  );

  if (type === "compact") {
    return (
      <div
        className={twMerge(
          "px-[15px] md:px-[25px] md:py-[37px] rounded-[10px]",
          "bg-gradient-130 from-smart-teal to-jade",
          "text-white text-center"
        )}
      >
        <div className="text-[30px] font-black">Try Now</div>
        <div className="text-[16px] mt-[18px] font-medium">
          Our customers make faster, more informed decisions
          <br />
          Get started with a complimentary Preview Pass today.
        </div>
        <div className="mt-[27px] flex flex-col justify-center">
          <div
            className={twMerge(
              "text-[15px] text-smart-teal font-bold",
              "h-[44px] px-[40px] py-[12px]",
              "bg-white rounded-full",
              "cursor-pointer"
            )}
            onClick={() => toggleModal("sign-up")}
          >
            TRY NOW
          </div>
          <p className="mt-[10px] mb-0">no credit card required</p>
        </div>
      </div>
    );
  } else if (type === "insight-signup") {
    return (
      <div className="p-[20px] pt-[15px] md:p-[40px] rounded-[16px] bg-gradient-130 from-smart-teal to-jade">
        <div className="flex flex-col md:flex-row">
          <div className="flex flex-1 flex-col text-white font-bold">
            <label className="text-[41px] md:text-[45px] leading-[53px] md:leading-[78px] md:mt-[30px]">
              {t(SIGN_UP_TEXTS.heading)}
            </label>
            <div className="text-[18px] leading-[22px] mt-[7px] mb-[19px]">
              {t(SIGN_UP_TEXTS.description)}
            </div>
            <div className="text-[15px] leading-[26px] mb-[24px]">
              <ul className="list-none m-0 p-0">
                {SIGN_UP_BENEFITS.map((benefit, index) => (
                  <li key={index}>
                    <FontAwesomeIcon icon={faCheckCircle} /> {t(benefit)}
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-[15px] leading-[22px]">
              {t(SIGN_UP_TEXTS.description2)}
            </p>
            <p className="text-[15px] leading-[22px]">
              {t(SIGN_UP_TEXTS.description3)}
            </p>
          </div>
          <div className="w-full md:w-[318px] md:ml-[41px] p-[24px] bg-white rounded-[16px]">
            {form}
          </div>
        </div>
        <div className="pt-[30px] flex justify-end">
          <div
            className={twMerge(
              "text-white font-bold text-center",
              "w-full md:w-[318px]"
            )}
          >
            Already have an account?{" "}
            <span
              className="text-white hover:text-white cursor-pointer underline"
              onClick={() => (onSwitch ? onSwitch() : toggleModal("sign-in"))}
            >
              Sign In Now
            </span>
          </div>
        </div>
        {customFooter}
      </div>
    );
  }

  if (border) {
    return (
      <div className={style.container}>
        <div className={style.header}>
          <label>PREVIEW PASS</label>
        </div>
        {form}
      </div>
    );
  }

  return form;
};

export default SignUpForm;

export const SIGN_UP_TEXTS = {
  heading: "Try Now",
  description:
    "Begin exploring Smartkarma's AI-augmented investing intelligence platform with a complimentary Preview Pass to:",
  description2:
    "Join 55,000+ investors, including top global asset managers overseeing $13+ trillion.",
  description3: "Upgrade later to our paid plans for full-access.",
};

export const SIGN_UP_BENEFITS = [
  "Unlock research summaries",
  "Follow top, independent analysts",
  "Receive personalised alerts",
  "Access Analytics, Events and more",
];

export const SIGN_UP_LOGOS = [
  {
    name: "Bloomberg",
    logo: (
      <Image
        src={bloombergLogo}
        alt="Bloomberg"
        layout="fixed"
        width={77}
        height={16}
      />
    ),
  },
  {
    name: "Societe Generale",
    logo: (
      <Image
        src={societeGeneraleLogo}
        alt="Societe Generale"
        layout="fixed"
        width={77}
        height={16}
      />
    ),
  },
  {
    name: "SGX",
    logo: (
      <Image src={sgxLogo} alt="SGX" layout="fixed" width={38} height={16} />
    ),
  },
  {
    name: "Sequoia",
    logo: (
      <Image
        src={sequoiaLogo}
        alt="Sequoia"
        layout="fixed"
        width={84}
        height={16}
      />
    ),
  },
  {
    name: "Interactive Broker",
    logo: (
      <Image
        src={interactiveBrokerLogo}
        alt="Interactive Broker"
        layout="fixed"
        width={85}
        height={23}
      />
    ),
  },
  {
    name: "Refinitiv",
    logo: (
      <Image
        src={refinitivLogo}
        alt="Refinitiv"
        layout="fixed"
        width={88}
        height={18}
      />
    ),
  },
];
